import { AitHeaderGroupData, AitRowGroupData } from "aic";

/**
 * Generate column IDs for a table using the first header or body row as a template.
 *
 * @param tableData - Table data which may contain `headerData` or `bodyData`.
 * @param tableData.headerData - Optional header group data used as the primary template for columns.
 * @param tableData.bodyData - Optional body row groups used as a fallback template for columns.
 * @returns Array of column ID strings generated from the first header or body row.
 */
export const generateColumnIds = <T>(tableData: {
  headerData?: AitHeaderGroupData<T> | false;
  bodyData?: AitRowGroupData<T>[];
}): string[] => {
  // Try to get column count and existing columnIds from header first
  if (
    tableData.headerData &&
    typeof tableData.headerData === "object" &&
    "rows" in tableData.headerData &&
    Array.isArray(tableData.headerData.rows) &&
    tableData.headerData.rows.length > 0
  ) {
    const firstRow = tableData.headerData.rows[0];
    /* istanbul ignore else */
    if ("cells" in firstRow && Array.isArray(firstRow.cells)) {
      return firstRow.cells.map((cell: { columnId?: string }) =>
        cell.columnId && cell.columnId !== "" ? cell.columnId : crypto.randomUUID(),
      );
    }
  }

  // Fall back to body data
  else if (
    tableData.bodyData &&
    Array.isArray(tableData.bodyData) &&
    tableData.bodyData.length > 0 &&
    tableData.bodyData[0].rows.length > 0
  ) {
    const firstRow = tableData.bodyData[0].rows[0];
    /* istanbul ignore else */
    if ("cells" in firstRow && Array.isArray(firstRow.cells)) {
      return firstRow.cells.map((cell: { columnId?: string }) =>
        cell.columnId && cell.columnId !== "" ? cell.columnId : crypto.randomUUID(),
      );
    }
  }

  // Return empty array if no columns exist
  return [];
};
